/**
 * @fileoverview Contract Clauses Management Page Component
 * 
 * Provides comprehensive CRUD interface for managing standard contract clauses.
 * Clauses are reusable contract terms that can be applied to multiple contracts.
 * This component integrates with jQuery DataTables for advanced table rendering
 * with sorting, pagination, and search capabilities.
 * 
 * @module components/pages/clauses
 * @requires React - UI framework with hooks
 * @requires react-bootstrap - Bootstrap 5 components
 * @requires react-select - Dropdown select component
 * @requires jquery - DOM manipulation and DataTables
 * @requires datatables.net - Advanced table plugin
 * @requires admin - API service for clause CRUD operations
 * 
 * @component
 * @example
 * { path: '/clauses', name: 'Clauses', element: <Clauses />, isPrivate: true }
 * 
 * Features:
 * - View all clauses in paginated DataTable (25 rows/page)
 * - Create new clause with name and description
 * - Edit existing clauses (name becomes read-only)
 * - Delete clauses with taxonomy association warnings
 * - Real-time text search across all fields
 * - Tooltip hints for edit/delete buttons
 * - Process loader during API operations
 * - Success messages via toast notifications
 */

import React, { useEffect, useState } from "react";
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { default as Select, components }  from "react-select";
import $ from "jquery";
import * as ReactDOM from 'react-dom';
import "datatables.net";
import {getFilenameFromURL,getUsername,formatDate} from "../../utils/common_functions"
import {API_SUCCESS_RESPONSE_CODE,API_SUCCESS_RESPONSE_CREATED_CODE} from "../../utils/config_mapping"
import CommonIcon from '../common/customicon';
import PageLoader from '../common/pageloader';
import ProcessLoader from '../common/processloader';
import "../../../node_modules/datatables.net-dt/css/dataTables.dataTables.css";
import {getAllClauses,createClause,deleteClause,updateClause} from "../../services/admin"
import Message from "../common/message";
import HeaderLogo  from "../structure/headerlogo";

/**
 * Clauses Component - Clause management interface
 * 
 * Manages standard contract clauses with full CRUD operations.
 * Displays in paginated DataTable with real-time search and edit/delete actions.
 * 
 * @component
 * @returns {React.ReactElement} Clauses management page
 */
export const Clauses = () => {
  // ============================================================================
  // Custom Components
  // ============================================================================
  
  /**
   * Option Component - react-select checkbox option
   * 
   * Custom dropdown option with checkbox for multi-select.
   * Currently unused but available for multi-clause selection features.
   * 
   * @param {Object} props - react-select props
   * @param {boolean} props.isSelected - Option selection state
   * @param {string} props.label - Display text
   * @returns {React.ReactElement} Checkbox with label
   */
  const Option = (props) => {
    return (
      <div>
        <components.Option {...props}>
          <input
            type="checkbox"
            checked={props.isSelected}
            onChange={() => null}
          />{" "}
          <label>{props.label}</label>
        </components.Option>
      </div>
    );
  };

  // ============================================================================
  // Modal State
  // ============================================================================
  
  // Add/Edit clause modal state
  const [addclausemodal, setAddClausesModal] = useState(false);
  const handleClose = () => { 
    setAddClausesModal(false); 
    $('body').trigger('click');
  };
  const handleShow = () => setAddClausesModal(true);
  
  // Delete confirmation modal state
  const [deletedocumentmodal, setDeleteClausesModal] = useState(false);
  const handleDeleteClose = () => setDeleteClausesModal(false);
  const handleDeleteShow = () => setDeleteClausesModal(true);
  
  // ============================================================================
  // Data State
  // ============================================================================
  
  const [loading, setLoading] = useState(true); // Loading state during fetch
  const [data, setData] = useState([]); // All clauses from API
  const [filteredData, setFilteredData] = useState([]); // Clauses displayed in table
  const [searchQuery, setSearchQuery] = useState(""); // Current search text
  const [filters, setFilters] = useState({
    filterstatus: null,
    filterTextSearch: ""
  });

  // ============================================================================
  // Form State
  // ============================================================================
  
  const initialState = {
    clauseName: "",
    clauseDescription: "",
    clauseId: "" // Empty = create, non-empty = edit
  }

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});

  /**
   * handleDelete - Delete clause via API
   * 
   * Retrieves clause ID from button, calls delete API, reloads list on success.
   * Shows warning if clause is associated with taxonomies.
   * 
   * @async
   * @function handleDelete
   * @param {Event} event - Click event from delete button
   * @returns {Promise<void>}
   */
  const handleDelete = async (event) => {
    const dataId = event.target.getAttribute('data-id');
    const targetElement = document.getElementById("app_err");
    if(dataId){
      try {
        const result = await deleteClause(dataId);
        if(result!==undefined){
          if (result.code === API_SUCCESS_RESPONSE_CODE) {
            handleDeleteClose()
            fetchData();
            if (targetElement) {
              ReactDOM.unmountComponentAtNode(targetElement);
            }
            ReactDOM.render(<Message type='success' message={result.data.message} />, targetElement);
          }
        }
      } catch (error) {
        console.error("Error deleting clause:", error);
      }
    }
  };

  /**
   * handleSaveClause - Create or update clause
   * 
   * Creates new clause if clauseId is empty, updates existing if ID provided.
   * Reloads clause list and shows success message on completion.
   * 
   * @async
   * @function handleSaveClause
   * @param {Object} pmData - Clause data object
   * @returns {Promise<void>}
   */
  const handleSaveClause = async (pmData) => {
    const targetElement = document.getElementById("app_err");
    if(pmData.clauseId===""){
      // CREATE new clause
      const result = await createClause(pmData);
      if (result.code === API_SUCCESS_RESPONSE_CREATED_CODE) {
        fetchData();
        handleClose()
        if (targetElement) {
          ReactDOM.unmountComponentAtNode(targetElement);
        }
        ReactDOM.render(<Message type='success' message={result.data.message} />, targetElement);
      } else {
        console.warn("Unexpected response status:", result.status);
      }
    }else{
      // UPDATE existing clause
      const result = await updateClause(pmData.clauseId,pmData);
      if (result.code === API_SUCCESS_RESPONSE_CODE) {
        fetchData();
        handleClose()
        if (targetElement) {
          ReactDOM.unmountComponentAtNode(targetElement);
        }
        ReactDOM.render(<Message type='success' message={result.data.message} />, targetElement);
      } else {
        console.warn("Unexpected response status:", result.status);
      }
    }
  };

  /**
   * fetchData - Retrieve all clauses from server
   * 
   * Makes GET request to retrieve all contract clauses.
   * Updates data and filteredData state with the same data.
   * Shows/hides loading spinner during fetch.
   * 
   * @async
   * @function fetchData
   * @returns {Promise<void>}
   */
  const fetchData = async () => {
    // Hide process loader if visible (from previous operation)
    $(".page_pro_load").hide();
    // Show full page loading spinner
    setLoading(true);
    try {
      // Retrieve all clauses from server
      const result = await getAllClauses();
      // Check for successful response (200 OK)
      if (result.code === API_SUCCESS_RESPONSE_CODE) {
        // Store all clauses
        setData(result.data);
        // Initialize filtered data with all clauses (no filters applied yet)
        setFilteredData(result.data);
      } else {
        console.warn("Unexpected response status:", result.status);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    finally {
      // Hide loading spinner after fetch completes
      setLoading(false);
    };
  };
  
  /**
   * Initial Data Load Effect
   * 
   * Runs once when component mounts (empty dependency array).
   * Fetches all clauses from the server for initial page load.
   * 
   * @effect
   */
  useEffect(() => {
    // Fetch all clauses on component mount
    fetchData();
  }, []); // Empty dependency array ensures this runs only once on mount


  /**
   * DataTable Initialization Effect
   * 
   * Dependencies: [filteredData]
   * Recreates the jQuery DataTable whenever filteredData changes.
   * Renders table cells with React components for edit/delete actions.
   * Attaches click handlers to edit and delete buttons.
   * Destroys table on unmount to prevent memory leaks.
   * 
   * @effect
   * @depends filteredData - Table recreates when filtered data changes
   */
  useEffect(() => {
    if (filteredData.length > -1) {
      // Initialize jQuery DataTable with configuration
      const table = $("#contract_grid").DataTable({
        // Collapse table height to content
        scrollCollapse: true,
        scrollX: true,
        // Default page size: show 25 clauses per page
        pageLength: 25,
        // Dynamic height calculation (currently commented out)
        // scrollY: $(window).height()-($("#table_height").height()+180),
        // Data array: populated from filtered data state
        data: filteredData,
        // Destroy existing DataTable before recreating
        // Necessary when data changes due to search filter
        destroy: true,
        // Sort order: empty array disables automatic sorting
        // Users can click column headers to sort
        order: [],
        // Column Definitions: maps data properties to table columns
        columns: [
          // Clause Name Column: primary identifier
          { data: "clauseName", title: "Clause Name", width: "40px" },
          // Clause Description Column: full text description
          { data: "clauseDescription", title: "Clause Description", width: "40px" },
          // Clause Type Column (commented out)
          // { data: "clauseType", title: "Clause Type", width: "40px" },
          // Created By Column: username of creator
          { data: "createdBy", title: "Created By", width: "40px" },
          // Created At Column (commented out)
          // { data: "createdAt", title: "Created At", width: "40px" },
          // Created At with Date Formatting Column
          // Note: render function has bug (return statement before conditional)
          {
            data: "createdAt",
            title: "Created At",
            width: "40px",
            render: function (data, type, row) {
              return data;
              if (data) {
                return formatDate(data)
              }
            }},
            {
              data: "taxonomyName", // Field name from your data
              title: "Taxonomy Names",
              width: "40px",
              render: function (data, type, row) {
                if (data) {
                  return data
                }else {
                  return "-"
                }
              }},
          {
            title: "Actions",
            orderable: false, // Disable sorting for this column
            data: null,
            width: "60px", 
            createdCell: function (td, cellData, rowData, row, col) {
              // Clear the default HTML and replace it with React-rendered content
              ReactDOM.render(
                <>
                  <OverlayTrigger
                    placement="top"
                    trigger={['hover', 'click']} // Add 'click' to ensure it closes on click
                    overlay={<Tooltip id="view-top">Edit</Tooltip>}
                  >
                    <a href="javascript:void(0);" className="view-btn" data-id={rowData._id} data-name={rowData.clauseName} data-desc={rowData.clauseDescription}>
                      <i className="datatable-pencil-icon"></i>
                    </a>
                  </OverlayTrigger>
                  &nbsp;
                  <OverlayTrigger
                    trigger={['hover', 'click']} // Add 'click' to ensure it closes on click
                    placement="top"
                    overlay={<Tooltip id="delte-top">Delete</Tooltip>}
                  >
                  <a href="javascript:void(0);" className="delete-btn" data-bs-toggle="modal"
                     data-bs-target="#confirmationModal" data-id={rowData._id} data-name={rowData.clauseName} data-taxonomynamelist={rowData.taxonomyName} >
                    <i className="datatable-delete-icon"></i>
                  </a></OverlayTrigger>
                  &nbsp;
                </>,
                td // Render the content directly into the table cell
              );
            }
          }
                 ],
        fixedColumns: {
          start: 1,
          end: 1
      },
        paging: true,
        searching: false,
        ordering: true,
        responsive: true,
        fixedHeader: true,
        language: {
          // Message shown when no data matches filters
          emptyTable: "No data found matching the filters"
        }
      });

      // ========================================================================
      // Edit Button Click Handler
      // ========================================================================
      // Triggered when user clicks the edit pencil icon in actions column
      $("#contract_grid").on("click", ".view-btn", function () {
        // Extract clause data from button's data attributes
        const id = $(this).data("id");
        const name = $(this).data("name");
        const desc = $(this).data("desc");
        // Build edit form state with clause data
        const editState = {
          clauseName: name,
          clauseDescription: desc,
          clauseId: id // Non-empty ID signals edit mode (not create)
        }
        setFormData(editState);
        handleShow()
      });

      $("#contract_grid").on("click", ".delete-btn", function () {
        // Open delete confirmation modal
        handleDeleteShow()
        // Extract clause data from button's data attributes
        const id = $(this).data("id");
        const name = $(this).data("name");
        const taxonomynamelist = $(this).data("taxonomynamelist");
        // Delay to allow modal to open before populating
        setTimeout(function() {
          // Set clause ID for deletion
          $("#btn_delete").attr("data-id", id);
          // Display clause name
          $("#document_name").html(name);
          // Show association warning if applicable
          if(taxonomynamelist===undefined){
            $("#not_tax_name").html("")
          }else {
            $("#not_tax_name").html("Note: This clause is associated with '"+taxonomynamelist+"' Taxonomies.")
          }
        }, 200);
       });

      // Cleanup: Destroy DataTable on unmount
      return () => {
        table.destroy();
      };
    }
  }, [filteredData]); // Recreate table when filtered data changes

  // ============================================================================
  // Form Handlers and Validation
  // ============================================================================
  
  /**
   * handleSearch - Update search query and apply filters
   * 
   * Triggered by search input onChange event.
   * Updates search query state and calls applyFilters.
   * 
   * @function handleSearch
   * @param {Event} e - Change event from search input
   * @returns {void}
   */
  const handleSearch = (e) => {
    // Update search query state
    setSearchQuery(e.target.value);
    // Apply filters with new search value
    applyFilters(e.target.value, filters);
  };

  /**
   * validateForm - Check form data for required fields
   * 
   * Validates that clause name is provided.
   * Clause description is optional.
   * 
   * @function validateForm
   * @returns {boolean} True if all required fields are valid
   */
  const validateForm = () => {
    const newErrors = {};
    // Clause name is required
    if (!formData.clauseName) newErrors.clauseName = "Clause name is required.";
    // Clause description validation disabled (currently optional)
    // if (!formData.clauseDescription) newErrors.clauseDescription = "Clause description is required.";
    // Update error state with validation results
    setErrors(newErrors);
    // Return true if no errors found
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * handleInputChange - Update form field value and clear error
   * 
   * Called whenever user types in a form field.
   * Updates the corresponding formData property and clears validation error.
   * 
   * @function handleInputChange
   * @param {Event} e - Change event from form input
   * @returns {void}
   */
  const handleInputChange = (e) => {
    // Extract field name and new value from input element
    const { name, value } = e.target;
    // Update formData with new value for this field
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear any validation error for this field
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  /**
   * resetForm - Clear form and open modal for new clause creation
   * 
   * Resets all form fields to initial state (empty strings).
   * Opens the add/edit clause modal.
   * Called when user clicks "Add Clause" button.
   * 
   * @function resetForm
   * @returns {void}
   */
  const resetForm = (e) => {
    // Open the add/edit clause modal
    handleShow()
    // Reset form to initial empty state (enables create mode)
    setFormData(initialState);
  };

  /**
   * handleSubmit - Validate and submit clause form
   * 
   * Called when user clicks Create/Update button in modal.
   * Validates form, shows process loader, adds metadata, and calls save handler.
   * 
   * @function handleSubmit
   * @param {Event} e - Submit event from form
   * @returns {void}
   */
  const handleSubmit = (e) => {
    // Get current logged-in user name (currently unused)
    const logged_user_name = getUsername();
    // Prevent default form submission behavior
    e.preventDefault();
    // Validate form - return early if validation fails
    if (!validateForm()) return;
    // Show process loader to indicate save in progress
    $(".page_pro_load").show();
    // Set fixed clause type (all clauses are "List" type)
    formData.clauseType = "List"
    // Initialize empty taxonomy IDs array
    formData.taxonomyIds = [];
    // Call API to create or update clause
    handleSaveClause(formData)
  };

  /**
   * handleFilterChange - Update filter state and reapply filters
   * 
   * Called when any filter control changes.
   * Updates the filter state and immediately reapplies all filters.
   * 
   * @function handleFilterChange
   * @param {string} filterName - Name of the filter property to update
   * @param {*} value - New filter value
   * @returns {void}
   */
  const handleFilterChange = (filterName, value) => {
    // Update filter state with new value
    setFilters((prevState) => {
      // Create new filters object with updated value
      // Create new filters object with updated value
      const newFilters = { ...prevState, [filterName]: value };
      // Apply filters immediately with new filter state
      applyFilters(newFilters);
      // Return updated filters state
      return newFilters;
    });
  };

  /**
   * applyFilters - Filter clause list based on filter values
   * 
   * Applies text search filter across multiple clause fields.
   * Currently only filterTextSearch is implemented.
   * 
   * Search Algorithm:
   * - Case-insensitive partial matching
   * - Searches across: clauseName, clauseDescription, clauseType, createdBy, createdAt, _id
   * - Uses OR logic: matches if ANY field contains search text
   * - Updates filteredData state which triggers DataTable recreation
   * 
   * @function applyFilters
   * @param {Object} newFilters - Filter state object
   * @param {string} newFilters.filterTextSearch - Search query text
   * @returns {void}
   */
  const applyFilters = (newFilters) => {
    // Start with all clauses
    let filteredDataTemp = data;

    // Apply text search filter if search query is provided
    if (newFilters.filterTextSearch) {
      // Filter clauses to only those matching search text
      filteredDataTemp = filteredDataTemp.filter((doc) => {
        // Convert search text to lowercase for case-insensitive comparison
        const searchLower = newFilters.filterTextSearch.toLowerCase();
        // Return true if ANY field contains the search text
        return (
          doc.clauseName.toLowerCase().includes(searchLower) ||
          doc.clauseDescription.toLowerCase().includes(searchLower) ||
          doc.clauseType.toLowerCase().includes(searchLower) ||
          doc.createdBy.toLowerCase().includes(searchLower) ||
          doc.createdAt.toLowerCase().includes(searchLower) ||
          doc._id.toLowerCase().includes(searchLower)
        );
      });
    }
    // Update filteredData state (triggers DataTable recreation)
    setFilteredData(filteredDataTemp);
  };

  /**
   * resetFilters - Clear all filters and show all clauses
   * 
   * Resets search query and filter states to initial values.
   * Updates filteredData to show all clauses (no filtering).
   * 
   * @function resetFilters
   * @returns {void}
   */
  const resetFilters = () => {
    // Clear search query
    setSearchQuery('');
    // Reset all filters to initial state (all null/empty)
    setFilters({
      filterdocumentType: null,
      filterlicensor: null,
      filterexpiryDate: '',
      filterstatus: null
    });
    // Reset displayed data to show all clauses (no filtering)
    setFilteredData(data);
  };

  // ============================================================================
  // Render Section - JSX Layout
  // ============================================================================

  return (
    // Main content wrapper
    <div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
      {/* Page content section */}
      <div className="page-content">
        {/* Full-width fluid container */}
        <div className="container-fluid">
          {/* Header section */}
          <div id="table_height">
            {/* Title and description area */}
            <div className="flex-grow-1" id="row_heading">
              {/* Page title */}
              <div className="page-title">Contract Clauses</div>
              {/* Description of page purpose */}
              <p className="text-muted my-2 ff-secondary">
              Manage and organize standard contract clauses
              </p>
              {/* Error/success message container */}
              <div id="app_err"></div>
            </div>

            {/* Search and Action Buttons Row: Only show if clauses exist */}
            { data.length > 0 ? 
            <div className="row g-2 mb-4 mt-4" id="row_search">
              {/* Search Input Column */}
              <div className="col-sm-10">
              <div className="search-box">
                <input
                  type="text"
                  className="form-control"
                  id="searchClausess"
                  placeholder="Search Clausess..."  // This should work as expected
                  onChange={(e) => handleFilterChange('filterTextSearch', e.target.value)}
                />
                <i className="grid-search">
                <CommonIcon iconName="Search" size={"14px"}  />
                </i>
              </div>
            </div>
            <div className="col-sm-auto ms-auto">
              <div className="list-grid-nav hstack gap-1">
                <button className="btn btn-light" onClick={fetchData}>
                <CommonIcon iconName="RefreshCw" size={"14px"} color={"#000"} />
                </button>
                <button onClick={resetForm}
                  className="btn btn-primary uploadcontract-modal"
                  data-bs-toggle="modal"
                  data-bs-target="#addmemberModal"
                >
                  <CommonIcon iconName="Plus" size={"14px"} color="#fff" /> Add Clause
                </button>
              </div>
            </div>
          </div> : "" }

      <div className="row d-none" id="row_filter" >
      <div className="col-lg-1 mt-2">
      <CommonIcon iconName="Filter" size={"14px"}  /> Filters:
      </div>
      <div className="col-lg-3">
      {/* <Select
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{
              Option
            }}
            value={filters.filterdocumentType}
            onChange={(selectedOption) => handleFilterChange('filterdocumentType', selectedOption)}
            options={documentTypeOptions}
            placeholder="Clauses Type"
          /> */}
      </div>
      <div className="col-lg-3">
      {/* <Select
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{
              Option
            }}
            value={filters.filterlicensor}
            onChange={(selectedOption) => handleFilterChange('filterlicensor', selectedOption)}
            options={licensorOptions}
            placeholder="Licensor"
          /> */}
      </div>
      <div className="col-lg-3 mb-2">
      {/* <Select
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{
              Option
            }}
            value={filters.filterstatus}
            onChange={(selectedOption) => handleFilterChange('filterstatus', selectedOption)}
            options={statusoptions}
            placeholder="Status"
          /> */}
          </div>
      </div>
      </div>
          <div className="row">
            <div className="col-lg-12">
                  <div>
                    {/* DataTable */}
                    {loading ? (
                 <PageLoader />
            ) :  data.length > 0 ?
                (
                <div className="row  mt-4">
                  <div className="col-lg-12">
                    <table
                      id="contract_grid"
                      className="display"
                      style={{ width: "100%" }}
                    >
                      <thead>
                      </thead>
                    </table>
                  </div>
                </div>) : <div className="mt-5 card"><div className="text-center">
      <div className="no-doc-container my-4">
        {/* Bootstrap file-earmark icon with a warning sign */}
        <CommonIcon iconName="FileText" width="50" height="50" />
        <div className="mt-3">
          <i className="bi bi-exclamation-circle-fill text-danger"></i>
        </div>
      </div>
      <h4 className="text-muted mb-4">No Clause Found</h4>
      <button onClick={resetForm}
                  className="btn btn-primary uploadcontract-modal mb-4"
                  data-bs-toggle="modal"
                  data-bs-target="#addmemberModal"
                >
                  <CommonIcon iconName="Plus" size={"14px"} color="#fff" /> Add Clause
                </button>
                <br/>
                <br/>
    </div>
    </div> }
                  </div>
                </div>
              </div>
            {/* </div>
          </div> */}
        </div>
      </div>

      <Modal show={deletedocumentmodal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title><div className="page-title" id="confirmationModalLabel">
                Delete
              </div></Modal.Title>
        </Modal.Header>
        <Modal.Body>
        <label class="form-label">Are you sure want to delete <span id="document_name"></span> clause?</label>
         <p id="not_tax_name" className="bold"></p>
          </Modal.Body>
        <Modal.Footer>
        <button type="button" onClick={handleDeleteClose} className="btn btn-light" data-bs-dismiss="modal"><CommonIcon iconName="X" size={"14px"} /> Close</button>
                  <button onClick={handleDelete} id="btn_delete" data-id="1111" type="submit" className="btn btn-primary">
                    <CommonIcon iconName="Trash" size={"14px"} color="#fff" /> Delete
                  </button>
        </Modal.Footer>
      </Modal>

      <Modal show={addclausemodal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title> 
                  {formData.clauseId==="" ? "Create New Clause" : "Edit Clause"}
           </Modal.Title>
           <p className="modal-desc">
           {formData.clauseId==="" ? "Create a new standard clause template to your contract library" : "Edit clause template to your contract library"}
            </p>
        </Modal.Header>
        <Modal.Body>
        <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-lg-12">
                    <label className="form-label">Clause Name</label>
                    <input type="hidden" name="clauseId" value={formData.clauseId} />
                    <input
                      type="text"
                      name="clauseName"
                      className={formData.clauseId==="" ? "form-control" : "form-control-plaintext"}
                      value={formData.clauseName}
                      onChange={handleInputChange}
                      placeholder="Enter clause name"
                      readOnly={formData.clauseId==="" ? false : true}
                    />
                    {errors.clauseName && <div className="text-danger">{errors.clauseName}</div>}
                  </div>

                  <div className="col-lg-12">
                    <label className="form-label">Clause Description</label>
                    <textarea
                      type="text"
                      name="clauseDescription"
                       rows="5" cols="40"
                      className="form-control"
                      value={formData.clauseDescription}
                      onChange={handleInputChange}
                      placeholder="Enter clause description..."
                    />
                    {errors.clauseDescription && <div className="text-danger">{errors.clauseDescription}</div>}
                  </div>
                
                <div className="page_pro_load mb-2">{
                <ProcessLoader pmMessage={"PDF upload in progress, please wait..."}  />
                }</div>
                <div id="uploaderror"></div>
                </div>
              </form>
            </div>
        </Modal.Body>
        <Modal.Footer>
        <button type="button" onClick={handleClose} className="btn btn-light" data-bs-dismiss="modal"><i className="ri-close-line align-bottom me-1"></i> Close</button>
              <button onClick={handleSubmit} type="submit" className="btn btn-primary">
                    {formData.clauseId==="" ? "Create Clause" : "Update Clause"}
               </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};